import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { UserService } from './user.service';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { deepCopy } from 'src/app/shared/deep-copy';

@Injectable()
export class HttpService {

  private baseUrl: string;

  /**
   * Die Headeroptionen für die Http-Requests
   */
  private options: any;

  constructor(
    private http: HttpClient, private router: Router, private userService: UserService,
    private toaster: ToastrService
  ) {
    this.baseUrl = HttpService.formatBaseUrl();
    this.setHeaderOptions();
  }

  /**
   * Entfernt Trailing Slash der URL wenn vorhanden
   */
  public static formatBaseUrl(): string {
    const baseUrl = environment.baseUrl;
    if (baseUrl.charAt(baseUrl.length - 1) === '/') {
      return baseUrl.slice(0, -1);
    }
    return baseUrl;
  }

  /**
   * Setzt die Optionen für die Http-Header in der Klasse
   */
  private setHeaderOptions() {
    const headers = new Headers();
    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'application/json');

    this.options = {
      headers: headers,
      withCredentials: true
    };
  }

  /**
   * Gibt die BaseUrl der Anwendung zurück
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }
  
  /**
   * Führt einen POST-Request auf der übergebenen Route durch.
   * 
   * @param targetRoute Die API-Route
   * @param payload Der Payload
   * @param successCallback Die Success-Funktion
   * @param errorCallback Optionale Error-Funktion
   */
  post(targetRoute: string, payload: any, successCallback: Function, errorCallback?: Function) {
    const sub = this.http.post(this.baseUrl + targetRoute, payload, this.getOptions())
      .subscribe(
        (response) => {
          successCallback(response);
        },
        (error) => {
          if (typeof errorCallback === 'function') {
            errorCallback(error);
          }
        },
        () => {
          sub.unsubscribe();
        }
      );
  }

  /**
   * Führt einen GET-Request auf der übergebenen Route durch.
   * 
   * @param targetRoute Die API-Route
   * @param successCallback Die Success-Funktion
   * @param errorCallback Optionale Error-Funktion
   * @param params Optionale HTTP-Parameter
   */
  get(targetRoute: string, successCallback: Function, errorCallback?: Function, params?: HttpParams) {
    const sub = this.http.get(this.baseUrl + targetRoute, this.getOptions(params))
      .subscribe((response) => {
        successCallback(response);
      },
        (error: HttpErrorResponse) => {
          console.error(error);
          if (typeof errorCallback === 'function') {
            errorCallback(error);
          }
        },
        () => {
          sub.unsubscribe();
        }
      );
  }

  /**
   * Führt ein POST-Request ohne Callback aus und gibt direkt das Observable zurück.
   * 
   * @param targetRoute Die API-Route
   * @param payload Der Payload
   * @returns Observable
   */
  postWithoutCallback(targetRoute: string, payload: any): Observable<any> {
    return this.http.post(this.baseUrl + targetRoute, payload, this.getOptions());
  }

  private getOptions(params?: any): any {
    let options = deepCopy(this.options);
    if (params) {
      options.params = params;
    } else {
      options.params = null;
    }
    return options;
  }

  /**
   * Führt ein PATCH-Request ohne Callback aus und gibt direkt das Observable zurück.
   *
   * @param targetRoute Die API-Route
   * @param payload Der Payload
   * @returns Observable
   */
  patchWithoutCallback(targetRoute: string, payload: any): Observable<any> {
    return this.http.patch(this.baseUrl + targetRoute, payload, this.getOptions());
  }

  /**
   * Führt ein GET-Request ohne Callback aus und gibt direkt das Observable zurück.
   * 
   * @param targetRoute Die API-Route
   * @param params Die HTTP-Parameter.
   * @returns Observable
   */
  getWithoutCallback(targetRoute: string, params?: HttpParams): Observable<any> {
    return this.http.get(this.baseUrl + targetRoute, this.getOptions(params));
  }

  /**
   * Funktion zum Löschen (DELETE-Request) von Datensätzen auf dem Server.
   *
   * @param targetRoute Die API-Route
   * @param successCallback Success-Funktion
   * @param errorCallback Error-Funktion
   */
  delete(targetRoute: string, successCallback: Function, errorCallback?: Function) {
    const sub = this.http.delete(this.baseUrl + targetRoute, this.getOptions())
      .subscribe((response) => {
        successCallback(response);
      },
        (error) => {
          console.error(error);
          this.toaster.error(error.error.message ? error.error.message : error.statusText);
          if (typeof errorCallback === 'function') {
            errorCallback(error);
          }
        },
        () => {
          sub.unsubscribe();
        }
      );
  }

  /**
   * Führt ein DELETE-Request ohne Callback aus und gibt direkt das Observable zurück.
   * 
   * @param route Die API-Route
   */
  deleteWithoutCallback(route) {
    return this.http.delete(this.baseUrl + route, this.getOptions());
  }

  /**
   * Funktion zum Aktualisieren (PUT-Request) von Datensätzen auf dem Server.
   * 
   * @param targetRoute Die API-Route
   * @param successCallback Success-Funktion
   * @param errorCallback Error-Funktion
   */
  put(targetRoute: string, successCallback: Function, errorCallback?: Function) {
    const sub = this.http.put(this.baseUrl + targetRoute,  this.getOptions())
      .subscribe((response) => {
        successCallback(response);
      },
        (error) => {
          console.log(error);

          if (typeof errorCallback === 'function') {
            errorCallback(error);
          }
        },
        () => {
          sub.unsubscribe();
        }
      );
  }

  /**
   * Funktion zum Ändern (PATCH-Request) von Datensätzen auf dem Server.
   * 
   * @param targetRoute Die API-Route
   * @param payload Der Payload
   * @param successCallback Success-Funktion
   * @param errorCallback Error-Funktion
   */
  patch(targetRoute: string, payload: any, successCallback: Function, errorCallback?: Function) {
    const sub = this.http.patch(this.baseUrl + targetRoute, payload, this.getOptions())
      .subscribe(
        (response) => {
          successCallback(response);
        },
        (error) => {
          if (typeof errorCallback === 'function') {
            errorCallback(error);
          }
        },
        () => {
          sub.unsubscribe();
        }
      );
  }
}
